import {
  ActivityIcon,
  ClipboardListIcon,
  CogIcon,
  GraduationCapIcon,
  HardHatIcon,
  SettingsIcon,
  WindIcon,
  WrenchIcon,
  ZapIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Module = {
  number: string;
  title: string;
  subtitle: string;
  context: string;
  items: string[];
  result: string;
  icon: LucideIcon;
};

export const modules: Module[] = [
  {
    number: '1',
    title: 'Industrial Maintenance Fundamentals: The Foundation',
    subtitle: 'The foundation your students need to understand everything else',
    context:
      'Your first introduction to industrial maintenance can be transformative or overwhelming. This volume transforms technical concepts into something comprehensible and relevant from day one.',
    items: [
      'Fundamentals of Industrial Maintenance',
      'Role and Importance of Maintenance',
      'Maintenance Objectives',
      'Corrective Maintenance',
      'Preventive Maintenance',
      'Predictive Maintenance',
      'Condition-Based Maintenance',
      'Maintenance Strategies',
      'Industrial Equipment & Systems',
      'Maintenance Departments & Responsibilities',
      'Work Orders & Maintenance Documentation',
      'Basic Maintenance Planning',
      'Equipment Inspection Principles',
      'Maintenance Performance Concepts',
      'Introduction to Reliability',
      'Failure Concepts',
      'Maintenance Communication',
      'Continuous Improvement',
      'Classroom Applications & Review',
    ],
    result:
      'Your students understand industrial maintenance as a practical discipline that connects equipment science, strategic planning, and operational excellence — not an impossible list of terms to memorize.',
    icon: WrenchIcon,
  },
  {
    number: '2',
    title: 'Mechanical Systems & Equipment Maintenance',
    subtitle: 'Everything your students need to know about mechanical components and systems',
    context:
      'This is where many students struggle to build the mechanical foundation. This volume gives you the materials to help them truly understand mechanical maintenance.',
    items: [
      'Mechanical Maintenance Fundamentals',
      'Bearings',
      'Shafts',
      'Couplings',
      'Gears & Gear Systems',
      'Belt & Chain Drives',
      'Pumps',
      'Compressors',
      'Valves',
      'Mechanical Seals',
      'Lubrication Fundamentals',
      'Alignment Concepts',
      'Mechanical Wear',
      'Vibration Concepts',
      'Common Mechanical Failure Patterns',
      'Inspection & Condition Assessment',
      'Mechanical Troubleshooting Principles',
      'Maintenance Documentation',
      'Practical Classroom Applications',
      '15 worked mechanical maintenance examples',
    ],
    result:
      'Your students learn the mechanical principles that underpin equipment reliability and maintenance strategy.',
    icon: CogIcon,
  },
  {
    number: '3',
    title: 'Electrical Systems & Industrial Maintenance',
    subtitle: 'Electrical concepts and controls from a maintenance perspective',
    context:
      'Electrical systems are critical but complex. This volume gives you everything needed so your students grasp electrical maintenance clearly.',
    items: [
      'Electrical Maintenance Fundamentals',
      'Electrical Safety Awareness',
      'Basic Electrical Concepts',
      'Industrial Electrical Systems',
      'Motors',
      'Motor Control Concepts',
      'Electrical Panels',
      'Circuit Protection',
      'Relays & Contactors',
      'Sensors',
      'Switches',
      'Industrial Control Systems',
      'PLC Concepts',
      'Electrical Inspection Principles',
      'Common Electrical Faults',
      'Electrical Troubleshooting Reasoning',
      'Maintenance Documentation',
      'Preventive Maintenance Concepts',
      'Classroom Applications',
      '15 worked electrical maintenance examples',
    ],
    result: 'Your students understand how electrical systems operate and how to maintain them safely and effectively.',
    icon: ZapIcon,
  },
  {
    number: '4',
    title: 'Preventive & Predictive Maintenance',
    subtitle: 'How to move from reacting to failures to managing equipment proactively',
    context:
      'Predictive maintenance is the future of maintenance. This volume makes these strategies clear and applicable.',
    items: [
      'Preventive Maintenance Fundamentals',
      'Predictive Maintenance Fundamentals',
      'Condition-Based Maintenance',
      'Inspection Programs',
      'Maintenance Tasks',
      'Maintenance Frequencies & Planning Concepts',
      'Equipment Condition',
      'Wear & Degradation',
      'Vibration Monitoring Concepts',
      'Temperature Monitoring',
      'Lubrication Monitoring',
      'Visual Inspection',
      'Acoustic & Ultrasound Concepts',
      'Data Collection',
      'Trend Analysis',
      'Condition Indicators',
      'Identifying Abnormal Conditions',
      'Maintenance Decision-Making',
      'Classroom Examples & Applications',
      '15 practical predictive maintenance case studies',
    ],
    result:
      'Your students can design maintenance strategies based on equipment condition and understand the business case for proactive maintenance.',
    icon: ActivityIcon,
  },
  {
    number: '5',
    title: 'Hydraulic & Pneumatic Systems Maintenance',
    subtitle: 'Educational overview of hydraulic and pneumatic systems and their maintenance',
    context:
      'Hydraulic and pneumatic systems are found everywhere in industry. This volume makes them understandable.',
    items: [
      'Hydraulic System Fundamentals',
      'Pneumatic System Fundamentals',
      'Pressure, Flow & Force Concepts',
      'Pumps',
      'Compressors',
      'Cylinders & Actuators',
      'Valves',
      'Filters',
      'Hoses & Connections',
      'Hydraulic Fluids',
      'Air Preparation',
      'Contamination',
      'Leakage Concepts',
      'Common Failure Symptoms',
      'Preventive Maintenance',
      'Inspection Principles',
      'Troubleshooting Reasoning',
      'Safety Considerations',
      'Classroom Applications',
      '12 worked hydraulic and pneumatic examples',
    ],
    result:
      'Your students understand how hydraulic and pneumatic systems work and how to maintain them reliably.',
    icon: WindIcon,
  },
  {
    number: '6',
    title: 'Industrial Equipment Reliability & Failure Analysis',
    subtitle: 'Understanding equipment failures and developing reliability strategies',
    context:
      'Reliability analysis is crucial to maintenance strategy. This volume develops this thinking in your students.',
    items: [
      'Reliability Fundamentals',
      'Reliability in Industrial Maintenance',
      'Equipment Failure',
      'Failure Modes',
      'Failure Patterns',
      'Causes vs. Symptoms',
      'Root Cause Thinking',
      'Failure Analysis',
      'Recurring Failures',
      'Human & Organizational Factors',
      'Equipment History',
      'Maintenance Records',
      'Evidence Collection',
      'Reliability Indicators',
      'Downtime Concepts',
      'MTBF & MTTR Concepts',
      'Maintenance Strategy & Reliability',
      'Continuous Improvement',
      'Failure Prevention',
      'Classroom Analysis Exercises',
      '15 practical failure analysis case studies',
    ],
    result:
      'Your students can analyze why equipment fails, identify root causes, and recommend improvements to prevent recurrence.',
    icon: SettingsIcon,
  },
  {
    number: '7',
    title: 'Maintenance Planning, Scheduling & Work Management',
    subtitle: 'How to organize maintenance as a coordinated, planned activity',
    context:
      'Maintenance management is the discipline that ties everything together. This volume makes it practical and applicable.',
    items: [
      'Maintenance Planning Fundamentals',
      'Maintenance Scheduling',
      'Work Identification',
      'Work Requests',
      'Work Orders',
      'Job Prioritization',
      'Maintenance Backlog',
      'Resource Planning',
      'Labor Considerations',
      'Materials & Spare Parts',
      'Tools & Resource Coordination',
      'Planned vs. Unplanned Work',
      'Shutdown & Outage Concepts',
      'Scheduling Decisions',
      'Work Execution',
      'Maintenance Documentation',
      'Work Completion & Feedback',
      'Performance Review',
      'Continuous Improvement',
      'Classroom Planning Activities',
      '12 practical work management case studies',
    ],
    result:
      'Your students understand how to organize and execute maintenance work efficiently while managing resources and priorities.',
    icon: ClipboardListIcon,
  },
  {
    number: '8',
    title: 'Maintenance Safety, Risk Management & Industrial Best Practices',
    subtitle: 'Integrating safety and best practices into maintenance decision-making',
    context:
      'Safety and risk are fundamental to maintenance. This volume develops safety-conscious maintenance professionals.',
    items: [
      'Maintenance Safety Fundamentals',
      'Hazard Recognition',
      'Risk Awareness',
      'Risk Assessment Concepts',
      'Safe Work Principles',
      'Personal Protective Equipment',
      'Energy Hazard Awareness',
      'Lockout/Tagout Concepts',
      'Mechanical Hazards',
      'Electrical Hazards',
      'Hydraulic & Pneumatic Hazards',
      'Chemical Hazard Awareness',
      'Working at Height Awareness',
      'Confined Space Awareness',
      'Hot Work Awareness',
      'Human Factors',
      'Communication & Coordination',
      'Safety Culture',
      'Incident Learning',
      'Industrial Best Practices',
      'Classroom Safety Scenarios',
      '15 practical safety and risk case studies',
    ],
    result:
      'Your students understand that maintenance safety is built on hazard recognition, risk assessment, and continuous improvement.',
    icon: HardHatIcon,
  },
  {
    number: '9',
    title: 'Teaching Industrial Maintenance: Methods, Activities & Classroom Practice',
    subtitle: 'Your complete guide as an industrial maintenance education professional',
    context:
      'This volume gives you exactly what you need to plan, organize, and evaluate your entire year of industrial maintenance teaching.',
    items: [
      'Teaching Industrial Maintenance',
      'Learning Objectives',
      'Lesson Organization',
      'Explaining Technical Concepts',
      'Connecting Theory with Application',
      'Classroom Activities',
      'Problem-Based Learning',
      'Case-Based Learning',
      'Technical Questioning',
      'Student Discussions',
      'Troubleshooting Exercises',
      'Failure Analysis Activities',
      'Safety-Based Classroom Scenarios',
      'Assessment Strategies',
      'Student Feedback',
      'Technical Vocabulary Development',
      'Critical Thinking',
      'Decision-Making Activities',
      'Classroom Planning',
      'Teaching Applications & Projects',
      '10 complete integrated industrial maintenance projects',
    ],
    result:
      'You\'ll never wonder "what do I teach today?" again. Everything is planned and contextualized for maximum student learning.',
    icon: GraduationCapIcon,
  },
];
